import type { IPaginatedResponse } from '@/misc/IPaginatedResponse'
import type { IExcelColumn } from '@/models/Filtering/ExcelExportFilter'
import type { IMeterInterval } from '@/models/Sessions/IMeterInterval'
import type { IPayterSession } from '@/models/Sessions/IPayterSession'
import type { IPayterSessionRequest } from '@/models/Sessions/IPayterSessionRequest'
import type { IRatingSession } from '@/models/Sessions/IRatingSession'
import type { ISampledValue } from '@/models/Sessions/ISampledValue'
import type { ISendReceiptEmailRequest } from '@/models/Sessions/ISendReceiptEmailRequest'
import type { ISendReceiptEmailResponse } from '@/models/Sessions/ISendReceiptEmailResponse'
import type { ISession } from '@/models/Sessions/ISession'
import type { ISessionException } from '@/models/Sessions/ISessionException'
import type { ISessionTransaction } from '@/models/Sessions/ISessionTransaction'
import type { ISessionWhitelabelReceiptData } from '@/models/Sessions/ISessionWhitelabelReceiptData'
import type { ISessionWhitelabelReceiptDataRequest } from '@/models/Sessions/ISessionWhitelabelReceiptDataRequest'
import { inject, injectable } from 'inversify'
import type IApiClient from '../IApiClient'
import type { IApiRequestOptions } from '../IApiRequestOptions'
import { SERVICES_TYPES } from '../ServicesTypes'
import type ISessionService from './ISessionService'
import { processSessionsDateFilterParams } from '@/helpers/SessionHelper'

@injectable()
export class SessionService implements ISessionService {
  private readonly apiClient: IApiClient
  private readonly sessionBaseUri = 'sessions'

  constructor(@inject(SERVICES_TYPES.ApiClient) apiClient: IApiClient) {
    this.apiClient = apiClient
  }

  public async getAllSessions(queryParamStr: string, abortController?: AbortController): Promise<IPaginatedResponse<ISession>> {
    const processedQueryParams = processSessionsDateFilterParams(queryParamStr)
    return this.apiClient.get<IPaginatedResponse<ISession>>(`${this.sessionBaseUri}?${processedQueryParams}`, abortController)
  }

  public async getSlicedSessions(queryParamStr: string, abortController?: AbortController): Promise<IPaginatedResponse<ISession>> {
    const processedQueryParams = processSessionsDateFilterParams(queryParamStr)
    return this.apiClient.get<IPaginatedResponse<ISession>>(`${this.sessionBaseUri}-sliced?${processedQueryParams}`, abortController)
  }

  public async getRecentSessions(queryParamStr: string, abortController?: AbortController): Promise<IPaginatedResponse<ISession>> {
    const processedQueryParams = processSessionsDateFilterParams(queryParamStr)
    return this.apiClient.get<IPaginatedResponse<ISession>>(`${this.sessionBaseUri}-recent?${processedQueryParams}`, abortController)
  }

  public async getSession(sessionid: string, abortController?: AbortController, options?: IApiRequestOptions): Promise<ISession> {
    return this.apiClient.get<ISession>(`${this.sessionBaseUri}/${sessionid}`, abortController, options)
  }

  public async getSessionExceptions(sessionId: number): Promise<ISessionException[]> {
    return this.apiClient.get<ISessionException[]>(`${this.sessionBaseUri}/${sessionId}/exceptions`)
  }

  public async getSessionMeterIntervals(sessionId: number): Promise<IMeterInterval[]> {
    return this.apiClient.get<IMeterInterval[]>(`${this.sessionBaseUri}/${sessionId}/meter-intervals`)
  }

  public async getSessionTransactions(sessionId: number): Promise<ISessionTransaction[]> {
    return this.apiClient.get<ISessionTransaction[]>(`${this.sessionBaseUri}/${sessionId}/transactions`)
  }

  public async getSessionSampledValues(sessionId: number): Promise<ISampledValue[]> {
    return this.apiClient.get<ISampledValue[]>(`${this.sessionBaseUri}/${sessionId}/sampled-values`)
  }

  public async getPayterSession(payterSessionRequest: IPayterSessionRequest): Promise<IPayterSession> {
    return this.apiClient.post<IPayterSession>(`${this.sessionBaseUri}/payter`, payterSessionRequest)
  }

  public async exportSessions(size: number, queryParamStr: string, columnList: IExcelColumn[]): Promise<{ blob: Blob; fileName?: string }> {
    return this.apiClient.postBlobWithFileName(`${this.sessionBaseUri}/download/${size}?${queryParamStr}`, columnList)
  }

  public async reRateSession(sessionId: number): Promise<IRatingSession> {
    return this.apiClient.postNoBody(`${this.sessionBaseUri}/${sessionId}/rerate`)
  }

  public async reAuthorizeAndRerateSession(sessionId: number): Promise<IRatingSession> {
    return this.apiClient.postNoBody(`${this.sessionBaseUri}/${sessionId}/reauthorise-rerate`)
  }

  public async sendReceiptEmail(sendReceiptEmailRequest: ISendReceiptEmailRequest, sessionId: number): Promise<ISendReceiptEmailResponse> {
    return this.apiClient.post<ISendReceiptEmailResponse>(`${this.sessionBaseUri}/sendsessionreceiptemail`, sendReceiptEmailRequest)
  }

  public async getSessionWhitelabelReceiptData(sessionWhitelabelReceiptDataRequest: ISessionWhitelabelReceiptDataRequest): Promise<ISessionWhitelabelReceiptData> {
    return this.apiClient.post<ISessionWhitelabelReceiptData>(`${this.sessionBaseUri}/whitelabelreceiptdata`, sessionWhitelabelReceiptDataRequest)
  }

  public async approveSession(sessionId: number): Promise<IRatingSession> {
    return this.apiClient.postNoBody(`${this.sessionBaseUri}/${sessionId}/approve`)
  }

  public async rejectSession(sessionId: number): Promise<IRatingSession> {
    return this.apiClient.postNoBody(`${this.sessionBaseUri}/${sessionId}/reject`)
  }

  public async invalidateSession(sessionId: number): Promise<IRatingSession> {
    return this.apiClient.postNoBody(`${this.sessionBaseUri}/${sessionId}/invalidate`)
  }
}
